import { MediaPicker } from '@/components/media/MediaPicker';
import type { MediaRow } from '@/routes/media/types';
import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';

import { thumbUrl } from '@/lib/media/thumbUrl';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { TranslatableField } from '@/components/form/TranslatableField';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useResourceForm } from '@/lib/forms/useResourceForm';
import { useListParams } from '@/lib/list/useListParams';

import {
  USEFUL_LINK_GROUPS,
  type UsefulLinkGroup,
  type UsefulLinkInput,
  type UsefulLinkRow,
} from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/useful-links`, gated on `settings.update`.
 *
 * The tiles of docs/01-INFORMATION-ARCHITECTURE.md §3.7 and §13.8: the
 * ministry, the accreditation centre, Erasmus+, the partner universities. §3
 * gives `settings.update` to the Super Admin alone, so a Content Editor reaches
 * this screen through the sidebar and finds it read-only — which
 * `ResourceListScreen` derives from the permission by itself.
 */
export function UsefulLinksPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Editor recordId={rest} />;
}

function List() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const list = useListParams('sort_order');

  const columns: Column<UsefulLinkRow>[] = [
    {
      key: 'label',
      label: t('usefulLinks.columns.label'),
      render: (row) => (
        <div className="flex min-w-52 items-center gap-3">
          {/* The logo is the whole tile on the public page, so a missing one
              is worth seeing here rather than discovering there. */}
          {thumbUrl(row.logo) ? (
            <img src={thumbUrl(row.logo) ?? ''} alt="" className="h-8 w-8 rounded object-contain" />
          ) : (
            <span className="bg-surface-subtle grid h-8 w-8 place-items-center rounded text-caption text-muted">
              {'—'}
            </span>
          )}

          <div>
            <span className="font-semibold text-primary">{valueFor(row.label, locale)}</span>
            <span className="mt-0.5 block truncate text-caption text-muted">{row.url}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'group',
      label: t('usefulLinks.columns.group'),
      render: (row) => <Chip tone="neutral">{t(`usefulLinks.groups.${row.group}`)}</Chip>,
    },
    {
      key: 'is_visible',
      label: t('usefulLinks.columns.visible'),
      render: (row) => (
        <span className="text-caption text-secondary">
          {t(row.is_visible ? 'common.yes' : 'common.no')}
        </span>
      ),
    },
  ];

  return (
    <ResourceListScreen
      resource="useful-links"
      permission="settings"
      title={t('nav.items.usefulLinks')}
      intro={t('usefulLinks.intro')}
      columns={columns}
      labelFor={(row) => valueFor(row.label, locale)}
      defaultSort="sort_order"
      emptyTitle={t('usefulLinks.empty.title')}
      emptyBody={t('usefulLinks.empty.body')}
      newLabel={t('usefulLinks.new')}
      deleteTitle={t('usefulLinks.delete.title')}
      deleteBody={(label) => t('usefulLinks.delete.body', { label })}
      filters={
        <Select
          label={t('usefulLinks.columns.group')}
          hideLabel
          value={String(list.params.group ?? '')}
          onChange={(event) => list.setFilter('group', event.target.value || null)}
          options={[
            { value: '', label: t('usefulLinks.anyGroup') },
            ...USEFUL_LINK_GROUPS.map((group) => ({
              value: group,
              label: t(`usefulLinks.groups.${group}`),
            })),
          ]}
        />
      }
    />
  );
}

interface Fields {
  label: TranslatedValue;
  group: UsefulLinkGroup;
  url: string;
  logo_media_id: number | null;
  logo: MediaRow | null;
  target: '_self' | '_blank';
  is_visible: boolean;
  sort_order: number;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { form, values, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<UsefulLinkRow, Fields, UsefulLinkInput>({
      resource: 'useful-links',
      recordId,
      empty,
      toFields,
      toInput,
      errorFields: ['label', 'group', 'url', 'logo_path', 'target', 'sort_order'],
    });

  const { register, setValue, formState } = form;

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-3xl space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-h2 font-extrabold text-primary">
          {isNew ? t('usefulLinks.new') : t('usefulLinks.edit')}
        </h1>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/useful-links')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <TranslatableField
        label={t('usefulLinks.fields.label')}
        name="label"
        value={values.label}
        onChange={(next) => setValue('label', next, { shouldDirty: true })}
        error={formState.errors.label?.message}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label={t('usefulLinks.columns.group')}
          hint={t('usefulLinks.fields.groupHint')}
          {...register('group')}
          error={formState.errors.group?.message}
          options={USEFUL_LINK_GROUPS.map((group) => ({
            value: group,
            label: t(`usefulLinks.groups.${group}`),
          }))}
        />

        <Select
          label={t('menus.fields.target')}
          {...register('target')}
          options={[
            { value: '_blank', label: t('menus.targets._blank') },
            { value: '_self', label: t('menus.targets._self') },
          ]}
        />

        {/* Absolute: every one of these leaves the site, and a relative path
            would link into our own 404 — which the server refuses. */}
        <Input
          {...register('url')}
          label={t('usefulLinks.fields.url')}
          hint={t('usefulLinks.fields.urlHint')}
          error={formState.errors.url?.message}
          className="sm:col-span-2"
        />

        <MediaPicker
          label={t('usefulLinks.fields.logo')}
          hint={t('staff.fields.photoHint')}
          value={values.logo}
          onChange={(mediaId, media) => {
            setValue('logo_media_id', mediaId, { shouldDirty: true });
            setValue('logo', media, { shouldDirty: true });
          }}
          error={formState.errors.logo_media_id?.message}
        />

        <Input
          {...register('sort_order', { valueAsNumber: true })}
          type="number"
          min={0}
          label={t('content.fields.order')}
          hint={t('content.fields.orderHint')}
        />
      </div>

      <label className="flex items-center gap-2 text-body-sm text-primary">
        <input
          type="checkbox"
          {...register('is_visible')}
          className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        />
        {t('usefulLinks.fields.visible')}
      </label>

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('common.saving') : t('common.save')}
      </Button>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(): Fields {
  return {
    label: {},
    group: 'home',
    url: '',
    logo_media_id: null,
    logo: null,
    // `_blank` by default: these all leave the site.
    target: '_blank',
    is_visible: true,
    sort_order: 0,
  };
}

function toFields(row: UsefulLinkRow): Fields {
  return {
    label: row.label,
    group: row.group,
    url: row.url,
    logo_media_id: row.logo_media_id ?? null,
    logo: row.logo ?? null,
    target: row.target,
    is_visible: row.is_visible,
    sort_order: row.sort_order,
  };
}

function toInput(form: Fields): UsefulLinkInput {
  return {
    label: form.label,
    group: form.group,
    url: form.url.trim(),
    logo_media_id: form.logo_media_id,
    target: form.target,
    is_visible: form.is_visible,
    sort_order: Number(form.sort_order),
  };
}
